// api/form2316API.js
import axiosInstance from './axiosConfig';

const form2316API = {
    // Get all Form 2316 records for a client
    getByClient: async (clientId, params = {}) => {
        try {
            console.log('Fetching Form 2316 records for client:', clientId);
            const response = await axiosInstance.get(`/form2316/client/${clientId}`, { params });
            console.log('Form 2316 records fetched:', response.data.data?.records?.length || 0);
            return response.data;
        } catch (error) {
            console.error('Form 2316 API Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to fetch Form 2316 records';
        }
    },

    // Create Form 2316 record
    createRecord: async (clientId, recordData) => {
        try {
            console.log('Creating Form 2316 record:', {
                clientId,
                recordData
            });

            const response = await axiosInstance.post(`/form2316/client/${clientId}`, recordData);
            console.log('Form 2316 record created:', response.data);
            return response.data;
        } catch (error) {
            console.error('Form 2316 Creation Error:', {
                error: error.response?.data || error.message,
                status: error.response?.status
            });
            throw error.response?.data?.message || 'Failed to create Form 2316 record';
        }
    },

    // Create batch Form 2316 records
    createBatch: async (clientId, records, year) => {
        try {
            console.log('Creating batch Form 2316 records:', {
                clientId,
                recordCount: records.length,
                year
            });

            const payload = {
                records,
                year: parseInt(year)
            };

            const response = await axiosInstance.post(`/form2316/client/${clientId}/batch`, payload);
            console.log('Form 2316 batch created:', response.data);
            return response.data;
        } catch (error) {
            console.error('Form 2316 Batch Creation Error:', {
                error: error.response?.data || error.message,
                status: error.response?.status
            });
            throw error.response?.data?.message || 'Failed to create batch Form 2316 records';
        }
    },

    // Update Form 2316 record
    update: async (recordId, recordData) => {
        try {
            const response = await axiosInstance.put(`/form2316/${recordId}`, recordData);
            return response.data;
        } catch (error) {
            console.error('Form 2316 Update Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to update Form 2316 record';
        }
    },

    // Delete Form 2316 record
    delete: async (recordId) => {
        try {
            const response = await axiosInstance.delete(`/form2316/${recordId}`);
            return response.data;
        } catch (error) {
            console.error('Form 2316 Delete Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to delete Form 2316 record';
        }
    },

    // Get Form 2316 summary
    getSummary: async (clientId, params = {}) => {
        try {
            const response = await axiosInstance.get(`/form2316/summary/client/${clientId}`, { params });
            return response.data;
        } catch (error) {
            console.error('Form 2316 Summary Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to fetch Form 2316 summary';
        }
    },

    // Get single Form 2316 record
    getById: async (recordId) => {
        try {
            const response = await axiosInstance.get(`/form2316/${recordId}`);
            return response.data;
        } catch (error) {
            console.error('Form 2316 Get Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to fetch Form 2316 record';
        }
    },

    // Get available years
    getYears: async (clientId) => {
        try {
            const response = await axiosInstance.get(`/form2316/years/client/${clientId}`);
            return response.data;
        } catch (error) {
            console.error('Form 2316 Years Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to fetch Form 2316 years';
        }
    },

    // Record tax payment
    recordPayment: async (recordId, paymentData) => {
        try {
            const response = await axiosInstance.post(`/form2316/${recordId}/pay`, paymentData);
            return response.data;
        } catch (error) {
            console.error('Form 2316 Payment Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to record tax payment';
        }
    },

    // Certify record
    certify: async (recordId, certificationData) => {
        try {
            const response = await axiosInstance.put(`/form2316/${recordId}/certify`, certificationData);
            return response.data;
        } catch (error) {
            console.error('Form 2316 Certification Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to certify Form 2316 record';
        }
    },

    // Calculate tax
    calculateTax: async (params) => {
        try {
            const response = await axiosInstance.get('/form2316/calculate-tax', { params });
            return response.data;
        } catch (error) {
            console.error('Form 2316 Tax Calculation Error:', error.response?.data || error.message);
            throw error.response?.data?.message || 'Failed to calculate tax';
        }
    },

    // Helper functions for Form 2316
    getExemptionStatusOptions: () => {
        return [
            { value: 'Z', label: 'Z - Zero Exemption' },
            { value: 'S/ME', label: 'S/ME - Single/Married with no dependent' },
            { value: 'ME1', label: 'ME1 - Married with 1 qualified dependent' },
            { value: 'ME2', label: 'ME2 - Married with 2 qualified dependents' },
            { value: 'ME3', label: 'ME3 - Married with 3 qualified dependents' },
            { value: 'ME4', label: 'ME4 - Married with 4 qualified dependents' },
            { value: 'S1', label: 'S1 - Single with 1 qualified dependent' },
            { value: 'S2', label: 'S2 - Single with 2 qualified dependents' },
            { value: 'S3', label: 'S3 - Single with 3 qualified dependents' },
            { value: 'S4', label: 'S4 - Single with 4 qualified dependents' }
        ];
    },

    getCivilStatusOptions: () => {
        return [
            { value: 'Single', label: 'Single' },
            { value: 'Married', label: 'Married' },
            { value: 'Married with Qualified Dependents', label: 'Married with Qualified Dependents' },
            { value: 'Head of Family', label: 'Head of Family' },
            { value: 'Widow/Widower', label: 'Widow/Widower' }
        ];
    },

    getPeriodCoveredOptions: () => {
        return [
            { value: 'January-December', label: 'January - December (Full Year)' },
            { value: 'January-June', label: 'January - June (1st Half)' },
            { value: 'July-December', label: 'July - December (2nd Half)' },
            { value: 'Other', label: 'Other Period' }
        ];
    },

    getFrequencyOfPaymentOptions: () => {
        return [
            { value: 'Monthly', label: 'Monthly' },
            { value: 'Semi-Monthly', label: 'Semi-Monthly' },
            { value: 'Weekly', label: 'Weekly' },
            { value: 'Daily', label: 'Daily' }
        ];
    },

    getTaxTableOptions: () => {
        return [
            { value: '2023 and onwards', label: '2023 and onwards (TRAIN Law)' },
            { value: '2022', label: '2022' },
            { value: '2021', label: '2021' },
            { value: '2020', label: '2020' },
            { value: '2018-2019', label: '2018-2019' },
            { value: '2015-2017', label: '2015-2017' }
        ];
    },

    getStatusOptions: () => {
        return [
            { value: 'Draft', label: 'Draft', color: 'gray' },
            { value: 'Submitted', label: 'Submitted', color: 'blue' },
            { value: 'Verified', label: 'Verified', color: 'yellow' },
            { value: 'Certified', label: 'Certified', color: 'green' },
            { value: 'Cancelled', label: 'Cancelled', color: 'red' }
        ];
    },

    // Calculate tax based on 2023 tax table
    calculateTaxLocal: (taxableIncome, exemptionStatus = 'Z') => {
        const exemptionTable = {
            'Z': 0,
            'S/ME': 50000,
            'ME1': 75000,
            'ME2': 100000,
            'ME3': 125000,
            'ME4': 150000,
            'S1': 50000,
            'S2': 75000,
            'S3': 100000,
            'S4': 125000
        };
        
        const exemptionAmount = exemptionTable[exemptionStatus] || 0;
        const netTaxableIncome = Math.max(0, taxableIncome - exemptionAmount);
        
        let taxDue = 0;
        
        if (netTaxableIncome <= 250000) {
            taxDue = 0;
        } else if (netTaxableIncome <= 400000) {
            taxDue = (netTaxableIncome - 250000) * 0.15;
        } else if (netTaxableIncome <= 800000) {
            taxDue = 22500 + (netTaxableIncome - 400000) * 0.20;
        } else if (netTaxableIncome <= 2000000) {
            taxDue = 102500 + (netTaxableIncome - 800000) * 0.25;
        } else if (netTaxableIncome <= 8000000) {
            taxDue = 402500 + (netTaxableIncome - 2000000) * 0.30;
        } else {
            taxDue = 2202500 + (netTaxableIncome - 8000000) * 0.35;
        }
        
        return {
            exemptionAmount: exemptionAmount,
            netTaxableIncome: parseFloat(netTaxableIncome.toFixed(2)),
            taxDue: parseFloat(taxDue.toFixed(2))
        };
    },

    // Format TIN
    formatTIN: (tin) => {
        if (!tin) return '';
        const digits = tin.replace(/\D/g, '');
        if (digits.length === 12) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}-${digits.slice(9, 12)}`;
        }
        return tin;
    },

    // Parse TIN
    parseTIN: (formattedTIN) => {
        if (!formattedTIN) return '';
        return formattedTIN.replace(/\D/g, '');
    },

    // Format currency
    formatCurrency: (amount) => {
        if (amount === null || amount === undefined || isNaN(amount)) return '₱0.00';
        return `₱${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }
};

export default form2316API;